// src/pages/admin/PermissionsPage.js
import React, { useState, useEffect, useCallback } from "react";
import authService from "../../services/authService";
import { useToast } from "../../components/common/Toast";
import { IconShield, IconPlus, IconTrash, IconX } from "../../components/common/Icons";

const CATEGORIES = ["all", "admin", "roster", "stats", "scores", "schedule", "general"];
const CAT_COLORS = {
  admin: "bg-red-500/15 text-red-400 border-red-500/20",
  roster: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  stats: "bg-green-500/15 text-green-400 border-green-500/20",
  scores: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  schedule: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  general: "bg-onyx-700/20 text-onyx-400 border-onyx-600/20",
};

export default function PermissionsPage() {
  const toast = useToast();
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ codename: "", name: "", min_level: 4, category: "general", description: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await authService.getPermissions();
    if (res.success) setPerms(res.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const res = await authService.createPermission(form);
    if (res.success) {
      toast("Permission created", "success");
      setShowCreate(false);
      setForm({ codename: "", name: "", min_level: 4, category: "general", description: "" });
      load();
    } else {
      const msg = typeof res.error === "object" ? Object.values(res.error).flat().join(", ") : String(res.error);
      toast(msg, "error");
    }
  };

  const handleDelete = async (perm) => {
    if (!window.confirm(`Delete "${perm.codename}"?`)) return;
    const res = await authService.deletePermission(perm.id);
    if (res.success) { toast("Deleted", "success"); load(); }
    else toast("Failed", "error");
  };

  const filtered = filter === "all" ? perms : perms.filter((p) => p.category === filter);

  const inputCls = "w-full px-3 py-2.5 bg-onyx-950 border border-onyx-700/50 rounded-lg text-onyx-100 font-body text-sm placeholder-onyx-600 focus:outline-none focus:border-tiger-500/50 transition-all";
  const labelCls = "block text-xs font-display text-onyx-500 tracking-wider uppercase mb-1.5";

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <IconShield size={22} className="text-tiger-500" />
          <h1 className="font-display text-2xl font-bold text-onyx-100 tracking-wider">PERMISSIONS</h1>
          <span className="text-sm font-mono text-onyx-500">{perms.length}</span>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-display font-semibold text-sm tracking-wider bg-gradient-to-r from-tiger-600 to-tiger-500 text-white hover:from-tiger-500 hover:to-tiger-400 transition-all">
          {showCreate ? <IconX size={16} /> : <IconPlus size={16} />}
          {showCreate ? "CANCEL" : "NEW"}
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5 mb-6">
        {CATEGORIES.map((cat) => (
          <button key={cat} onClick={() => setFilter(cat)}
            className={`px-3 py-1.5 rounded-lg text-xs font-display tracking-wider transition-all ${
              filter === cat ? "bg-tiger-500/15 text-tiger-400 border border-tiger-500/30" : "bg-onyx-800/40 text-onyx-400 border border-onyx-700/20 hover:bg-onyx-800/60"
            }`}>
            {cat.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-8 p-6 rounded-xl border border-tiger-500/20 bg-onyx-900/60 animate-slide-down">
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Codename</label>
              <input className={inputCls} value={form.codename} onChange={(e) => setForm({ ...form, codename: e.target.value })} placeholder="e.g. edit_lineup" required />
            </div>
            <div>
              <label className={labelCls}>Display Name</label>
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Edit Game Lineup" required />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.filter((c) => c !== "all").map((c) => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Min Level</label>
              <input className={inputCls} type="number" min={1} max={8} value={form.min_level}
                onChange={(e) => setForm({ ...form, min_level: parseInt(e.target.value) || 4 })} />
            </div>
            <div className="md:col-span-2">
              <button type="submit" className="px-6 py-2.5 rounded-lg font-display font-semibold text-sm tracking-wider bg-gradient-to-r from-tiger-600 to-tiger-500 text-white hover:from-tiger-500 hover:to-tiger-400 transition-all">CREATE</button>
            </div>
          </form>
        </div>
      )}

      {/* Permission grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((p) => (
          <div key={p.id} className="p-4 rounded-xl bg-onyx-900/50 border border-onyx-800/30 hover:border-onyx-700/50 transition-all group">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-onyx-100 truncate">{p.codename}</p>
                <p className="text-xs font-body text-onyx-500 mt-1 truncate">{p.name}</p>
              </div>
              <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                <span className={`text-xs font-mono px-1.5 py-0.5 rounded border ${CAT_COLORS[p.category] || CAT_COLORS.general}`}>
                  {p.category}
                </span>
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-onyx-700/30 text-onyx-400 border border-onyx-600/20">
                  L{p.min_level}
                </span>
              </div>
            </div>
            <div className="flex justify-end mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => handleDelete(p)} className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                <IconTrash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
